import { DateTime } from 'luxon'
import React, { useCallback, useMemo } from 'react'
import { useCardTransition } from '../../../providers/cardTransition/cardTransition'
import { VARIANTS } from '../../../types/Components.types'
import Button from '../../atoms/button/Button'
import Calendar, { ICalendarProps } from '../../atoms/calendar/Calendar'
import styles from './DatePicker.module.css'

export interface IDatePickerProps
  extends Omit<ICalendarProps, 'selected' | 'onSelect' | 'minDate' | 'maxDate' | 'disabledDays'> {
  /**
   * Initial selected date(s)
   */
  initialDate?: Date | Date[] | null

  /**
   * Callback when date is confirmed
   */
  onDateConfirm: (date: Date | Date[]) => void

  /**
   * Minimum selectable date
   * @default tomorrow
   */
  minDate?: Date | DateTime | 'today' | 'tomorrow'

  /**
   * Maximum selectable date
   * @default 2 months from today
   */
  maxDate?: Date | DateTime

  /**
   * Whether to close the card immediately on date selection (without confirmation button)
   * @default false
   */
  closeOnSelect?: boolean

  /**
   * Whether to show the confirm button
   * @default true
   */
  showConfirmButton?: boolean

  /**
   * Confirm button text
   * @default "Confirmar"
   */
  confirmButtonText?: string

  /**
   * Custom validation function that returns an error message if validation fails
   */
  onValidate?: (date: Date | Date[]) => string | null

  disabledDays?: number[]
}

const DatePicker = ({
  initialDate = null,
  onDateConfirm,
  minDate = 'tomorrow',
  maxDate,
  closeOnSelect = false,
  showConfirmButton = true,
  confirmButtonText = 'Confirmar',
  onValidate,
  multipleDateSelection = false,
  disabledDays = [],
  ...calendarProps
}: IDatePickerProps) => {
  const { closeCard } = useCardTransition()
  const [selected, setSelected] = React.useState<Date | Date[] | null>(initialDate)

  // Convert minDate to JS Date
  const computedMinDate = useMemo(() => {
    if (minDate === 'today') {
      return DateTime.local().startOf('day').toJSDate()
    }
    if (minDate === 'tomorrow') {
      return DateTime.local().plus({ days: 1 }).startOf('day').toJSDate()
    }
    if (minDate instanceof DateTime) {
      return minDate.toJSDate()
    }
    return minDate
  }, [minDate])

  // Convert maxDate to JS Date
  const computedMaxDate = useMemo(() => {
    if (!maxDate) {
      return DateTime.local().plus({ months: 2 }).endOf('month').startOf('day').toJSDate()
    }
    if (maxDate instanceof DateTime) {
      return maxDate.toJSDate()
    }
    return maxDate
  }, [maxDate])

  const handleDaySelected = useCallback(
    (value: Date) => {
      const newSelection = multipleDateSelection ? (Array.isArray(selected) ? [...selected, value] : [value]) : value

      // Validate if validator provided
      if (onValidate) {
        const error = onValidate(newSelection)
        if (error) {
          // Validation failed - don't update selection
          return
        }
      }

      setSelected(newSelection)

      // If closeOnSelect is true, confirm and close immediately
      if (closeOnSelect) {
        onDateConfirm(newSelection)
        closeCard?.()
      }
    },
    [closeOnSelect, closeCard, multipleDateSelection, onDateConfirm, onValidate, selected],
  )

  const handleContinueClick = useCallback(() => {
    if (selected) {
      onDateConfirm(selected)
      closeCard?.()
    }
  }, [closeCard, onDateConfirm, selected])

  const isConfirmDisabled = useMemo(() => {
    if (!selected) return true
    if (Array.isArray(selected) && selected.length === 0) return true
    return false
  }, [selected])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Calendar
          className={styles.calendar}
          selected={selected}
          onSelect={handleDaySelected}
          minDate={computedMinDate}
          maxDate={computedMaxDate}
          multipleDateSelection={multipleDateSelection}
          disabledDays={disabledDays as (0 | 1 | 2 | 3 | 4 | 5 | 6)[]}
          {...calendarProps}
        />
      </div>

      {showConfirmButton && (
        <div className={styles.actions}>
          <Button
            variant={VARIANTS.primary}
            className={styles.action}
            onClick={handleContinueClick}
            disabled={isConfirmDisabled}
          >
            {confirmButtonText}
          </Button>
        </div>
      )}
    </div>
  )
}

export default React.memo(DatePicker)
